import { useEffect, useState } from "react";
import { FilterList } from "@mui/icons-material";
import { Box, Button, CircularProgress, Divider, Tab, Tabs, Typography } from "@mui/material";

import { InvestmentMetricsSection } from "../components/InvestmentMetricsSection";
import { InvestmentPerformanceChart } from "../components/InvestmentPerformanceChart";

import { DrawerControl, FilterSideBar } from "../../../shared/components";
import { TabContext, TabPanel } from "@mui/lab";
import { OutputBenefitChart } from "../components/OutputBenefitChart";
import { GeographicView } from "../components/GeographicView";
import { useInvestmentOversight } from "../hooks/useInvestmentOversight";
import { useCapabilities } from "../../../hooks/useCapabilities";

export const InvestmentOversight = () => {

    const { isAllowedToViewPerformanceOverview, isAllowedToViewBenefitDelivery, isAllowedToViewNetworkAreas } = useCapabilities();

    const {
        benefitTableData, benefitChartData, isBenefitLoading, benefitError,
        investmentTableData, investmentChartData, isInvestmentLoading, investmentError,
        networkData, isNetworkLoading, networkError,
    } = useInvestmentOversight();

    const showTabs =
        isAllowedToViewPerformanceOverview ||
        isAllowedToViewBenefitDelivery ||
        isAllowedToViewNetworkAreas;

    const defaultTab = isAllowedToViewPerformanceOverview
        ? 'performance-overview'
        : isAllowedToViewBenefitDelivery
            ? 'benefit-delivery'
            : isAllowedToViewNetworkAreas
                ? 'network-areas-view'
                : '';

    const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
    const [currentTab, setCurrentTab] = useState(defaultTab);
    const handleChangeTab = (_e: React.SyntheticEvent, tab: string) => {
        setCurrentTab(tab);
    };

    const handleOpenFilter = () => setIsOpenFilter(true);
    const handleCloseFilter = () => setIsOpenFilter(false);

    useEffect(() => {
        if (defaultTab && currentTab === '') {
            setCurrentTab(defaultTab);

        }
    }, [defaultTab]);

    if (isInvestmentLoading || isBenefitLoading || isNetworkLoading) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    if (investmentError || benefitError || networkError) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <Typography variant="h6" color="error">
                    {(investmentError || benefitError || networkError)?.message || "Error loading data."}
                </Typography>
            </Box>
        )
    };


    return (
        <Box display={'flex'} flexDirection={'column'} height={"100%"} overflow={"hidden"}>
            <Box mb={2} mt={2} mx={2}>
                <InvestmentMetricsSection />
            </Box>
            {showTabs && <TabContext value={currentTab}>
                <Box display="flex" alignItems="center" alignContent="center" pr={2}>
                    <Tabs
                        value={currentTab}
                        onChange={handleChangeTab}
                        aria-label="Tabs"
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{ minHeight: "auto" }}
                    >
                        {isAllowedToViewPerformanceOverview && <Tab
                            sx={{ height: "auto", minHeight: "auto" }}
                            label={'Performance Overview'}
                            value={'performance-overview'}
                        />}
                        {isAllowedToViewBenefitDelivery && <Tab
                            sx={{ height: "auto", minHeight: "auto" }}
                            label={'Benefit Delivery'}
                            value={'benefit-delivery'}
                        />}
                        {isAllowedToViewNetworkAreas &&
                            <Tab
                                sx={{ height: "auto", minHeight: "auto" }}
                                label={'Network Areas View'}
                                value={'network-areas-view'}
                            />}
                    </Tabs>
                    <Button
                        startIcon={<FilterList />}
                        variant="text"
                        color="inherit"
                        sx={{ marginLeft: "auto" }}
                        onClick={handleOpenFilter}
                    >
                        FILTERS
                    </Button>
                </Box>
                <Divider />
                <Box px={2} pb={2} flex={1} overflow="hidden" height="100%" sx={{ overflowY: 'auto' }} mt={2}>
                    <TabPanel value="performance-overview" sx={{ p: 0, height: "100%" }}>
                        <InvestmentPerformanceChart chartData={investmentChartData} tableData={investmentTableData} />
                    </TabPanel>
                    <TabPanel value="benefit-delivery" sx={{ p: 0, height: "100%" }}>
                        <OutputBenefitChart chartData={benefitChartData} tableData={benefitTableData} />
                    </TabPanel>
                    <TabPanel value="network-areas-view" sx={{ p: 0, height: "100%" }}>
                        <GeographicView data={networkData} />
                    </TabPanel>
                </Box>
            </TabContext>
            }
            <DrawerControl
                title="Filter Records"
                subTitle="Please apply following filters to refine results"
                isOpen={isOpenFilter}
                handleClose={handleCloseFilter}
            >
                <FilterSideBar onClose={handleCloseFilter} hiddenFilters={['site', 'stakeholder']} />
            </DrawerControl>
        </Box>
    );
}